import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery, sendWithAuthRetry } from '@lib/baseQuery';
import { HttpError } from '@lib/httpError';
import { services } from '@config/services';
import { getValidToken } from '@lib/getValidToken';
import { ordersApi } from '@features/orders/api';
import { dispatchCacheAction } from '@lib/rtkQueryDispatch';
import type {
  PackingSlipResponse,
  Shipment,
  VendorShipmentDetail,
  VendorShipmentDetailsBulkResponse,
  VendorShipmentListItem,
  VendorShipmentListParams,
  VendorShipmentListResponse,
} from './types';

/**
 * Build the `/v1/vendor/shipments` query string for the standalone Shipments
 * search page. Mirrors the legacy `shipmentServices.getVendorShipments` param
 * set verbatim, including `sortDir` → `sortAsc` (bool) — matching the LIVE
 * request captured on portal.dev (`?page=1&pageSize=25&sortBy=createdDate&sortAsc=false`).
 */
function buildVendorShipmentListQuery(params: VendorShipmentListParams): string {
  const parts: string[] = [];
  const add = (key: string, value: string | number | boolean | null | undefined): void => {
    if (value === null || value === undefined || value === '') return;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };
  add('id', params.id);
  add('orderExternalId', params.orderExternalId);
  add('facilityId', params.facilityId);
  add('status', params.status);
  for (const status of params.statuses ?? []) add('statuses', status);
  add('isPollingStopped', params.isPollingStopped);
  add('isRejectedByVendor', params.isRejectedByVendor);
  add('isPendingVendorAcceptance', params.isPendingVendorAcceptance);
  add('isPendingMerchantAcceptance', params.isPendingMerchantAcceptance);
  add('startDate', params.startDate);
  add('endDate', params.endDate);
  add('searchAll', params.searchAll);
  add('merchantName', params.merchantName);
  add('merchantOrderId', params.merchantOrderId);
  add('vendorId', params.vendorId);
  add('page', params.page);
  add('pageSize', params.pageSize);
  add('sortBy', params.sortBy);
  if (params.sortDir === 'asc' || params.sortDir === 'desc') add('sortAsc', params.sortDir === 'asc');
  return parts.join('&');
}

/**
 * Wire-shape lock for the standalone Shipments read endpoints. Each builder
 * returns the exact `{ url, method }` the live legacy fires (captured on
 * portal.dev). Asserted with `toEqual` in `api.test.ts` so a drifted param
 * or path fails the test.
 */
/** One row in the bulk-reject request body. Reason is required by the UI for
 * bulk (unlike single reject), but the endpoint itself accepts it as optional. */
export interface VendorBulkRejectItem {
  shipmentId: string;
  reason?: string;
}

/** One row in the bulk-reject response. The endpoint echoes `shipmentId` on every
 * row so callers correlate outcomes by id rather than by array position. */
export interface VendorBulkRejectResponseRow {
  shipmentId: string;
  status?: number;
  response?: unknown;
}

export interface VendorBulkRejectResponse {
  bulk: VendorBulkRejectResponseRow[];
}

/** One row in the bulk-reassign request body. `facilityId` is the TARGET
 * facility — do not confuse with a list row's `facilityId`, which is the
 * shipment's CURRENT facility. */
export interface VendorBulkReassignItem {
  shipmentId: string;
  facilityId: string;
  reason?: string;
}

/** One row in the bulk-reassign response, echoing `shipmentId` like every
 * other bulk endpoint so callers correlate outcomes by id. */
export interface VendorBulkReassignResponseRow {
  shipmentId: string;
  status?: number;
  response?: unknown;
}

export interface VendorBulkReassignResponse {
  bulk: VendorBulkReassignResponseRow[];
}

/** One package (tracking entry) within a bulk-fulfill request item. Empty/absent
 * `orderItemIds` is the backend's "unspecified package" — it scopes to the whole
 * shipment rather than naming items. */
export interface VendorBulkFulfillPackage {
  orderItemIds?: string[];
  carrierTrackingNumber: string;
  carrierName?: string;
  carrierMethodName?: string;
  carrierLink?: string;
  shippedDate?: string;
  fulfilledDate?: string;
}

/** One row in the bulk-fulfill request body. Rows sharing a `shipmentId` must be
 * grouped into one item with multiple `packages` before submit — the API is
 * per-shipment, not per-package. */
export interface VendorBulkFulfillItem {
  shipmentId: string;
  packages: VendorBulkFulfillPackage[];
}

/** One row in the bulk-fulfill response, echoing `shipmentId` like every other
 * bulk endpoint so callers correlate outcomes by id. */
export interface VendorBulkFulfillResponseRow {
  shipmentId: string;
  status?: number;
  response?: unknown;
}

export interface VendorBulkFulfillResponse {
  bulk: VendorBulkFulfillResponseRow[];
}

export const shipmentRequestBuilders = {
  getVendorShipments: (params: VendorShipmentListParams) => ({
    url: `${services.shipment}/v1/vendor/shipments?${buildVendorShipmentListQuery(params)}`,
    method: 'GET' as const,
  }),
  getVendorShipmentById: (shipmentId: string) => ({
    url: `${services.shipment}/v1/vendor/${shipmentId}`,
    method: 'GET' as const,
  }),
  getPackingSlip: ({ shipmentId, regenerate }: { shipmentId: string; regenerate?: boolean }) => ({
    url: `${services.shipment}/v1/packing-slip/${shipmentId}/generate?format=A4&type=pdf${
      regenerate ? '&regenerate=true' : ''
    }`,
    method: 'GET' as const,
  }),

  /* ---- vendor WRITE actions (P2). Each URL+method+body captured LIVE on
     portal.dev 2026-05-30 (see playwright-temp/shipments-p2-wire-shapes.md);
     `retryVendorPolling` is the one non-vendor-namespaced endpoint and is
     rank-3 (no eligible shipment to capture live — same URL the order page
     uses). ---- */

  /** PATCH /shipment/v1/vendor/{id}/status?shipmentStatus=<Value> — null body,
   * status rides in the query string (LIVE-confirmed). */
  updateVendorShipmentStatus: ({ shipmentId, shipmentStatus }: { shipmentId: string; shipmentStatus: string }) => ({
    url: `${services.shipment}/v1/vendor/${shipmentId}/status?shipmentStatus=${encodeURIComponent(shipmentStatus)}`,
    method: 'PATCH' as const,
  }),

  /** POST /shipment/v1/vendor/{id}/reject — body `{ reason }` (or `{}` when
   * the optional reason is omitted, mirroring the legacy modal). */
  rejectVendorShipment: ({ shipmentId, reason }: { shipmentId: string; reason?: string }) => ({
    url: `${services.shipment}/v1/vendor/${shipmentId}/reject`,
    method: 'POST' as const,
    body: reason ? { reason } : {},
  }),

  /** POST /shipment/v1/vendor/shipments/reject/bulk — body `{ bulk: [{ shipmentId, reason? }] }`,
   * ≤100 rows. Each response row echoes its shipmentId. */
  bulkRejectVendorShipments: ({ bulk }: { bulk: VendorBulkRejectItem[] }) => ({
    url: `${services.shipment}/v1/vendor/shipments/reject/bulk`,
    method: 'POST' as const,
    body: { bulk },
  }),

  /** POST /shipment/v1/vendor/shipments/reassign/bulk — body
   * `{ bulk: [{ shipmentId, facilityId, reason? }] }`, ≤100 rows. `facilityId`
   * per row is the TARGET facility. Each response row echoes its shipmentId. */
  bulkReassignVendorShipments: ({ bulk }: { bulk: VendorBulkReassignItem[] }) => ({
    url: `${services.shipment}/v1/vendor/shipments/reassign/bulk`,
    method: 'POST' as const,
    body: { bulk },
  }),

  /** POST /shipment/v1/vendor/shipments/fulfill/bulk — body
   * `{ bulk: [{ shipmentId, packages: [...] }] }`, ≤100 items. Each response
   * row echoes its shipmentId. */
  bulkFulfillVendorShipments: ({ bulk }: { bulk: VendorBulkFulfillItem[] }) => ({
    url: `${services.shipment}/v1/vendor/shipments/fulfill/bulk`,
    method: 'POST' as const,
    body: { bulk },
  }),

  /** POST /shipment/v1/vendor/{id}/reassign — body `{ facilityId, orderItemIds }`. */
  reassignVendorShipment: ({
    shipmentId,
    facilityId,
    orderItemIds,
  }: {
    shipmentId: string;
    facilityId: string;
    orderItemIds: string[];
  }) => ({
    url: `${services.shipment}/v1/vendor/${shipmentId}/reassign`,
    method: 'POST' as const,
    body: { facilityId, orderItemIds },
  }),

  /** POST /shipment/v1/vendor/{id}/packages — body is the package ARRAY
   * (the legacy saga posts `packages` straight through). */
  createVendorPackages: ({ shipmentId, packages }: { shipmentId: string; packages: VendorPackageInput[] }) => ({
    url: `${services.shipment}/v1/vendor/${shipmentId}/packages`,
    method: 'POST' as const,
    body: packages,
  }),

  /** PUT /shipment/v1/vendor/{id}/package/{packageId}/tracking — body carries
   * the tracking fields only (packageId is in the path, not the body). */
  updateVendorPackageTracking: ({
    shipmentId,
    packageId,
    data,
  }: {
    shipmentId: string;
    packageId: string;
    data: VendorPackageTrackingData;
  }) => ({
    url: `${services.shipment}/v1/vendor/${shipmentId}/package/${packageId}/tracking`,
    method: 'PUT' as const,
    body: data,
  }),

  /** DELETE /shipment/v1/vendor/{id}/package/{packageId}. */
  deleteVendorPackage: ({ shipmentId, packageId }: { shipmentId: string; packageId: string }) => ({
    url: `${services.shipment}/v1/vendor/${shipmentId}/package/${packageId}`,
    method: 'DELETE' as const,
  }),

  /** POST polling/retry for the standalone shipment detail. The page is an SA +
   * vendor surface, so the route is role-scoped: vendors MUST use the
   * vendor-namespaced `/v1/vendor/{id}/polling/retry` (the non-vendor path 403s
   * for them), while SuperAdmins use the non-vendor `/v1/{id}/polling/retry`
   * (the same path the order page fires). */
  retryVendorPolling: ({ shipmentId, vendor }: { shipmentId: string; vendor: boolean }) => ({
    url: vendor
      ? `${services.shipment}/v1/vendor/${shipmentId}/polling/retry`
      : `${services.shipment}/v1/${shipmentId}/polling/retry`,
    method: 'POST' as const,
    body: {},
  }),

  /** POST /shipment/v1/vendor/{id}/items/{itemId}/images/{imageIndex} — URL-tab
   * replace, body `{ sourceUrl }`. File uploads use the `/file` endpoint. */
  uploadVendorShipmentItemImage: ({
    shipmentId,
    itemId,
    imageIndex,
    sourceUrl,
  }: {
    shipmentId: string;
    itemId: string;
    imageIndex: number;
    sourceUrl?: string;
  }) => ({
    url: `${services.shipment}/v1/vendor/${shipmentId}/items/${itemId}/images/${imageIndex}`,
    method: 'POST' as const,
    body: sourceUrl ? { sourceUrl } : {},
  }),

  /** POST /shipment/v1/{shipmentId} — create a package on the (merchant) shipment.
   * The backend expects a JSON **array** of CreatePackageDTO (legacy posts
   * `[{...}]`). Sending a bare object fails deserialization ("the JSON value
   * could not be converted to ...List`1[CreatePackageDTO]"). Mirror the legacy
   * saga shape exactly: array-wrapped, `carrierAccount: false`, and string/date
   * defaults so no required DTO field arrives null. */
  createPackage: ({ shipmentId, packageData }: { shipmentId: string; packageData: CreatePackageInput }) => {
    const nowIso = new Date().toISOString();
    return {
      url: `${services.shipment}/v1/${shipmentId}`,
      method: 'POST' as const,
      body: [
        {
          carrierName: packageData.carrierName || '',
          carrierMethodName: packageData.carrierMethodName || '',
          shippedDate: packageData.shippedDate || nowIso,
          fulfilledDate: packageData.fulfilledDate || nowIso,
          carrierTrackingNumber: packageData.carrierTrackingNumber || '',
          carrierLink: packageData.carrierLink || '',
          carrierAccount: false,
          orderItemIDs: packageData.orderItemIDs || [],
          packageOrderItems: packageData.packageOrderItems || [],
        },
      ],
    };
  },
};

/** Inputs the order-detail create-package modal collects; the request builder
 * serializes these into the array body the backend's `CreatePackageDTO` expects. */
export interface CreatePackageInput {
  orderItemIDs: string[];
  packageOrderItems: Array<{ orderItemId: string; quantity: number }>;
  carrierTrackingNumber: string;
  carrierName?: string;
  carrierMethodName?: string;
  carrierLink?: string;
  shippedDate?: string;
  fulfilledDate?: string;
}

/** One package object in the create-packages array body. */
export interface VendorPackageInput {
  // Optional — sent as null when blank (the endpoint rejects whitespace-only
  // strings but accepts null; see TECH-22148).
  carrierName: string | null;
  carrierMethodName: string | null;
  carrierTrackingNumber: string;
  carrierLink: string | null;
  carrierAccount: boolean;
  orderItemIDs: string[];
  packageOrderItems: Array<{ orderItemId: string; quantity: number }>;
  // Optional — the vendor packages endpoint accepts null dates.
  shippedDate: string | null;
  fulfilledDate: string | null;
}

/** Tracking fields sent on the package-tracking PUT (no packageId in body). */
export interface VendorPackageTrackingData {
  carrierTrackingNumber: string;
  // Optional — sent as null when blank (the endpoint accepts null; see TECH-22148).
  carrierName: string | null;
  carrierMethodName: string | null;
  carrierLink: string | null;
  // Optional — the vendor tracking endpoint accepts null dates.
  shippedDate: string | null;
  fulfilledDate: string | null;
  /** TECH-21837: add/remove items in the same PUT. The PUT replaces, so the
   * caller echoes the existing tracking fields above; these carry the item
   * change (`packageOrderItems` = the package's items after the change). */
  packageOrderItems?: Array<{ orderItemId: string; quantity: number }>;
  addOrderItems?: boolean;
  removeOrderItems?: boolean;
}

/** Vendor image-replace response. TECH-22216: returns the created fileId, not a SAS. */
export interface VendorImageUploadResponse {
  fileId?: string;
}

/**
 * Upload vendor image bytes to shipment-service, which proxies them to the
 * files-service validating upload. Plain fetch (not RTK) so the multipart body
 * isn't forced to JSON by baseQuery. See TECH-22216.
 */
export function uploadVendorShipmentItemImageFile(
  args: { shipmentId: string; itemId: string; imageIndex: number },
  file: File,
): Promise<VendorImageUploadResponse> {
  return sendWithAuthRetry(() => sendVendorImageUpload(args, file));
}

async function sendVendorImageUpload(
  args: { shipmentId: string; itemId: string; imageIndex: number },
  file: File,
): Promise<VendorImageUploadResponse> {
  const token = await getValidToken();
  const form = new FormData();
  form.append('file', file, file.name || 'upload');

  const response = await fetch(
    `${services.shipment}/v1/vendor/${args.shipmentId}/items/${args.itemId}/images/${args.imageIndex}/file`,
    {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    },
  );

  if (!response.ok) {
    throw new HttpError(`Image upload failed: ${response.status} ${response.statusText}`, response.status);
  }
  return (await response.json()) as VendorImageUploadResponse;
}

/**
 * Helper to cross-invalidate the orders cache when a shipment mutation
 * completes. RTK Query tags are scoped to a single createApi instance, so we
 * dispatch the orders-slice invalidation explicitly. Mirrors portal's saga
 * pattern where shipment writes also re-pull the order detail.
 * `dispatchCacheAction` routes through the project's single approved
 * type-erasure helper (see `@lib/rtkQueryDispatch`).
 */
const invalidateOrder =
  (orderId: string) =>
  async (_arg: unknown, { dispatch, queryFulfilled }: { dispatch: (a: unknown) => unknown; queryFulfilled: Promise<unknown> }) => {
    try {
      await queryFulfilled;
      dispatchCacheAction(
        dispatch,
        ordersApi.util.invalidateTags([{ type: 'Order', id: orderId }]),
      );
    } catch {
      // mutation already surfaced its own error; nothing to do here
    }
  };

/**
 * endpoints.
 * The order-detail page calls `getShipmentsByOrderId(orderId)` to populate
 * SummaryView. portal hits `GET /shipment/v1/order/{orderId}` (note: singular
 * `/order/`) and uses the array result directly.
 * Mutations mirror portal's saga set: every shipment-edit mutation invalidates
 * both the `ShipmentsByOrder` list AND the related `Order` tag so the order
 * detail page (header counts, items) refetches alongside the shipment list.
 */
export const shipmentsApi = createApi({
  reducerPath: 'shipmentsApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['Shipment', 'ShipmentsByOrder', 'VendorShipment', 'VendorShipmentList', 'PendingAcceptance'],
  endpoints: (builder) => ({
    /**
     * Standalone Shipments page — vendor shipment SEARCH (SA + vendor surface).
     * LIVE (portal.dev 2026-05-30, SA + vendor):
     *   GET /shipment/v1/vendor/shipments?page=1&pageSize=25&sortBy=createdDate&sortAsc=false → 200
     * Envelope: legacy reads `.shipments ?? .data` + `.total`; unwrapped explicitly below.
     */
    getVendorShipments: builder.query<VendorShipmentListResponse, VendorShipmentListParams>({
      query: (params) => shipmentRequestBuilders.getVendorShipments(params),
      transformResponse: (response: {
        shipments?: VendorShipmentListItem[];
        data?: VendorShipmentListItem[];
        total?: number;
      }): VendorShipmentListResponse => ({
        shipments: response.shipments ?? response.data ?? [],
        total: response.total ?? 0,
      }),
      providesTags: [{ type: 'VendorShipmentList', id: 'LIST' }],
    }),

    /**
     * Standalone Shipments page — vendor shipment DETAIL (scoped to the
     * authenticated vendor; SA sees all).
     * LIVE (portal.dev 2026-05-30): GET /shipment/v1/vendor/{shipmentId} → 200.
     * Envelope: FLAT object (legacy saga stores `resp` directly — no `.shipment`
     * wrapper). Explicit identity transform keeps the observed shape visible.
     */
    getVendorShipmentById: builder.query<VendorShipmentDetail, string>({
      query: (shipmentId) => shipmentRequestBuilders.getVendorShipmentById(shipmentId),
      transformResponse: (response: VendorShipmentDetail): VendorShipmentDetail => response,
      providesTags: (_result, _error, shipmentId) => [{ type: 'VendorShipment', id: shipmentId }],
    }),

    /**
     * Packing slip metadata for a shipment (the "Print Packing Slip" header
     * action). LIVE: GET /shipment/v1/packing-slip/{shipmentId}/generate
     * ?format=A4&type=pdf[&regenerate=true] → `{ packingSlips: [{ fileId,
     * downloadUrl }] }`. Lazy — only fired when the modal opens; the modal
     * retries with `regenerate=true` when the slip isn't generated yet
     * (404 / empty list), mirroring the legacy saga.
     */
    getPackingSlip: builder.query<PackingSlipResponse, { shipmentId: string; regenerate?: boolean }>({
      query: (args) => shipmentRequestBuilders.getPackingSlip(args),
      transformResponse: (response: PackingSlipResponse): PackingSlipResponse => response,
    }),

    getShipmentsByOrderId: builder.query<Shipment[], string>({
      query: (orderId) => ({
        url: `${services.shipment}/v1/order/${orderId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, orderId) => [
        { type: 'ShipmentsByOrder', id: orderId },
      ],
    }),
    /** PATCH /shipment/v1/{shipmentId} body { shipmentStatus,... } */
    updateShipment: builder.mutation<
      Shipment,
      { orderId: string; shipmentId: string; data: Partial<Shipment> & Record<string, unknown> }
    >({
      query: ({ shipmentId, data }) => ({
        url: `${services.shipment}/v1/${shipmentId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),
    /** PATCH /shipment/v1/{shipmentId}/cancel body { cancelReason } */
    cancelShipment: builder.mutation<
      Shipment,
      { orderId: string; shipmentId: string; data: { cancelReason: string } }
    >({
      query: ({ shipmentId, data }) => ({
        url: `${services.shipment}/v1/${shipmentId}/cancel`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),
    /** POST /shipment/v1/{shipmentId}/resubmit */
    resubmitShipment: builder.mutation<unknown, { orderId: string; shipmentId: string }>({
      query: ({ shipmentId }) => ({
        url: `${services.shipment}/v1/${shipmentId}/resubmit`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),
    /** POST /shipment/v1/{shipmentId}/polling/retry */
    retryPolling: builder.mutation<unknown, { orderId: string; shipmentId: string }>({
      query: ({ shipmentId }) => ({
        url: `${services.shipment}/v1/${shipmentId}/polling/retry`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),
    /**
     * POST /shipment/v1/{shipmentId}/unassign
     *
     * The service unassigns a single item when the body carries `orderItemId`,
     * and every item on the shipment when the body is empty. There is no
     * subset form: omitting `orderItemId` deletes the whole shipment, so only
     * leave it out when the caller really means "unassign all".
     */
    unassignItems: builder.mutation<
      unknown,
      { orderId: string; shipmentId: string; orderItemId?: string }
    >({
      query: ({ shipmentId, orderItemId }) => ({
        url: `${services.shipment}/v1/${shipmentId}/unassign`,
        method: 'POST',
        body: orderItemId ? { orderItemId } : {},
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),
    /**
 * POST /shipment/v1/assign body { orderId, facilityId?, orderItemIds }
 * Creates a new shipment with the supplied items. When `facilityId` is
 * omitted the routing engine picks one automatically (mirrors portal's
 * "Automatic" mode in CreateShipmentModal).
 */
    assignItemsToShipment: builder.mutation<
      unknown,
      { orderId: string; facilityId?: string; orderItemIds: string[] }
    >({
      query: ({ orderId, facilityId, orderItemIds }) => ({
        url: `${services.shipment}/v1/assign`,
        method: 'POST',
        body: { orderId, facilityId: facilityId ?? '', orderItemIds },
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),
    /**
 * POST /shipment/v1/{shipmentId} — creates a new package on the shipment.
 * Mirrors portal's createPackage saga.
 */
    createPackage: builder.mutation<
      unknown,
      {
        orderId: string;
        shipmentId: string;
        packageData: CreatePackageInput;
      }
    >({
      query: ({ shipmentId, packageData }) => shipmentRequestBuilders.createPackage({ shipmentId, packageData }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),

    /** PATCH /shipment/v1/{shipmentId}/package/{packageId} — edit tracking. */
    updatePackage: builder.mutation<
      unknown,
      {
        orderId: string;
        shipmentId: string;
        packageId: string;
        data: {
          carrierTrackingNumber?: string;
          carrierName?: string;
          carrierMethodName?: string;
          carrierLink?: string;
          shippedDate?: string;
          fulfilledDate?: string;
          /** Add/remove items in the same PATCH. The body passes
           * straight through, so these ride alongside any tracking edits. */
          packageOrderItems?: Array<{ orderItemId: string; quantity: number }>;
          addOrderItems?: boolean;
          removeOrderItems?: boolean;
        };
      }
    >({
      query: ({ shipmentId, packageId, data }) => ({
        url: `${services.shipment}/v1/${shipmentId}/package/${packageId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),

    /** DELETE /shipment/v1/{shipmentId}/package/{packageId} — delete tracking. */
    deletePackage: builder.mutation<
      unknown,
      { orderId: string; shipmentId: string; packageId: string }
    >({
      query: ({ shipmentId, packageId }) => ({
        url: `${services.shipment}/v1/${shipmentId}/package/${packageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),

    /**
     * POST /shipment/v1/reroute
     *   body { orderId, shipmentId, orderItems?: [{ id }], vendorId?, facilityId?, reason? }
     *
     * The service reroutes only the items named in `orderItems`, and every item
     * on the shipment when the list is missing, which deletes the shipment.
     * The items carry no `quantity`: the reroute workflow refuses one.
     */
    rerouteShipment: builder.mutation<
      unknown,
      {
        orderId: string;
        shipmentId: string;
        itemIds?: string[];
        vendorId?: string;
        facilityId?: string;
        reason?: string;
      }
    >({
      query: ({ orderId, shipmentId, itemIds, vendorId, facilityId, reason }) => ({
        url: `${services.shipment}/v1/reroute`,
        method: 'POST',
        body: {
          orderId,
          shipmentId,
          orderItems: itemIds?.map((id) => ({ id })),
          vendorId,
          facilityId,
          reason,
        },
      }),
      invalidatesTags: (_r, _e, { orderId }) => [{ type: 'ShipmentsByOrder', id: orderId }],
      onQueryStarted: (arg, api) => invalidateOrder(arg.orderId)(arg, api),
    }),

    /* -------------------------------------------------------------------- */
    /* Standalone vendor-shipment WRITE actions (P2).                       */
    /* All target the shipment-service `/v1/vendor/...` family and refresh  */
    /* the detail by invalidating its `VendorShipment` tag — mirroring the  */
    /* legacy sagas, which re-pull `getVendorShipmentById` after each write.*/
    /* Wire shapes captured LIVE (see shipments-p2-wire-shapes.md).         */
    /* -------------------------------------------------------------------- */

    /** PATCH /shipment/v1/vendor/{id}/status?shipmentStatus=<Value>. */
    updateVendorShipmentStatus: builder.mutation<
      unknown,
      { shipmentId: string; shipmentStatus: string }
    >({
      query: (args) => shipmentRequestBuilders.updateVendorShipmentStatus(args),
      invalidatesTags: (_r, _e, { shipmentId }) => [{ type: 'VendorShipment', id: shipmentId }],
    }),

    /** POST /shipment/v1/vendor/{id}/reject — body `{ reason }` (optional). */
    rejectVendorShipment: builder.mutation<unknown, { shipmentId: string; reason?: string }>({
      query: (args) => shipmentRequestBuilders.rejectVendorShipment(args),
      invalidatesTags: (_r, _e, { shipmentId }) => [{ type: 'VendorShipment', id: shipmentId }],
    }),

    /** POST /shipment/v1/vendor/shipments/reject/bulk — per-row results, each
     * echoing its shipmentId. Refreshes the standalone shipments list. */
    bulkRejectVendorShipments: builder.mutation<VendorBulkRejectResponse, { bulk: VendorBulkRejectItem[] }>({
      query: (args) => shipmentRequestBuilders.bulkRejectVendorShipments(args),
      invalidatesTags: [{ type: 'VendorShipmentList', id: 'LIST' }],
    }),

    /** POST /shipment/v1/vendor/{id}/reassign — body `{ facilityId, orderItemIds }`. */
    reassignVendorShipment: builder.mutation<
      unknown,
      { shipmentId: string; facilityId: string; orderItemIds: string[] }
    >({
      query: (args) => shipmentRequestBuilders.reassignVendorShipment(args),
      invalidatesTags: (_r, _e, { shipmentId }) => [{ type: 'VendorShipment', id: shipmentId }],
    }),

    /** POST /shipment/v1/vendor/shipments/reassign/bulk — per-row results, each
     * echoing its shipmentId. Refreshes the standalone shipments list. */
    bulkReassignVendorShipments: builder.mutation<
      VendorBulkReassignResponse,
      { bulk: VendorBulkReassignItem[] }
    >({
      query: (args) => shipmentRequestBuilders.bulkReassignVendorShipments(args),
      invalidatesTags: [{ type: 'VendorShipmentList', id: 'LIST' }],
    }),

    /** POST /shipment/v1/vendor/shipments/fulfill/bulk — per-row results, each
     * echoing its shipmentId. Refreshes the standalone shipments list. */
    bulkFulfillVendorShipments: builder.mutation<
      VendorBulkFulfillResponse,
      { bulk: VendorBulkFulfillItem[] }
    >({
      query: (args) => shipmentRequestBuilders.bulkFulfillVendorShipments(args),
      invalidatesTags: [{ type: 'VendorShipmentList', id: 'LIST' }],
    }),

    /** POST /shipment/v1/vendor/{id}/packages — body is the package array. */
    createVendorPackages: builder.mutation<
      unknown,
      { shipmentId: string; packages: VendorPackageInput[] }
    >({
      query: (args) => shipmentRequestBuilders.createVendorPackages(args),
      invalidatesTags: (_r, _e, { shipmentId }) => [{ type: 'VendorShipment', id: shipmentId }],
    }),

    /** PUT /shipment/v1/vendor/{id}/package/{packageId}/tracking. */
    updateVendorPackageTracking: builder.mutation<
      unknown,
      { shipmentId: string; packageId: string; data: VendorPackageTrackingData }
    >({
      query: (args) => shipmentRequestBuilders.updateVendorPackageTracking(args),
      invalidatesTags: (_r, _e, { shipmentId }) => [{ type: 'VendorShipment', id: shipmentId }],
    }),

    /** DELETE /shipment/v1/vendor/{id}/package/{packageId}. */
    deleteVendorPackage: builder.mutation<unknown, { shipmentId: string; packageId: string }>({
      query: (args) => shipmentRequestBuilders.deleteVendorPackage(args),
      invalidatesTags: (_r, _e, { shipmentId }) => [{ type: 'VendorShipment', id: shipmentId }],
    }),

    /** POST polling/retry from the standalone detail. Route is role-scoped (see
     * the request builder): vendor → `/v1/vendor/{id}`, SA → `/v1/{id}`. */
    retryVendorPolling: builder.mutation<unknown, { shipmentId: string; vendor: boolean }>({
      query: (args) => shipmentRequestBuilders.retryVendorPolling(args),
      invalidatesTags: (_r, _e, { shipmentId }) => [{ type: 'VendorShipment', id: shipmentId }],
    }),

    /** URL-tab image replace (`{ sourceUrl }`). File uploads use
     * uploadVendorShipmentItemImageFile. */
    uploadVendorShipmentItemImage: builder.mutation<
      VendorImageUploadResponse,
      { shipmentId: string; itemId: string; imageIndex: number; sourceUrl?: string }
    >({
      query: (args) => shipmentRequestBuilders.uploadVendorShipmentItemImage(args),
      invalidatesTags: (_r, _e, { shipmentId }) => [{ type: 'VendorShipment', id: shipmentId }],
    }),

    /** POST /shipment/v1/vendor/shipments/details — batch fetch full shipment
     * details for the bulk Export CSV action. On-demand, no cache/invalidation. */
    getShipmentsDetails: builder.mutation<VendorShipmentDetailsBulkResponse, { shipmentIds: string[] }>({
      query: (body) => ({
        url: `${services.shipment}/v1/vendor/shipments/details`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetVendorShipmentsQuery,
  useLazyGetVendorShipmentsQuery,
  useGetVendorShipmentByIdQuery,
  useLazyGetPackingSlipQuery,
  useGetShipmentsByOrderIdQuery,
  useUpdateShipmentMutation,
  useCancelShipmentMutation,
  useResubmitShipmentMutation,
  useRetryPollingMutation,
  useUnassignItemsMutation,
  useRerouteShipmentMutation,
  useAssignItemsToShipmentMutation,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
  useUpdateVendorShipmentStatusMutation,
  useRejectVendorShipmentMutation,
  useBulkRejectVendorShipmentsMutation,
  useReassignVendorShipmentMutation,
  useBulkReassignVendorShipmentsMutation,
  useBulkFulfillVendorShipmentsMutation,
  useCreateVendorPackagesMutation,
  useUpdateVendorPackageTrackingMutation,
  useDeleteVendorPackageMutation,
  useRetryVendorPollingMutation,
  useUploadVendorShipmentItemImageMutation,
  useGetShipmentsDetailsMutation,
} = shipmentsApi;
